<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row justify-content-center">
        <div class="col col-md-8 col-lg-6">
          <h1 class="text-center">Create Investigation</h1>
          <ErrorSummary :error="error" />
          <div class="card mb-4">
            <h5 class="card-header">Details</h5>
            <div class="card-body">
              <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
                <div class="form-group">
                  <label>Investigation Id</label>
                  <input
                    v-model="investigationReferenceCode"
                    type="text"
                    maxlength="255"
                    class="form-control"
                    :disabled="loadingMessage"
                    @keyup.enter="submit"
                    @blur="getInvestigationReferenceCodeDuplicate"
                    data-bs-toggle="tooltip"
                    data-bs-delay="500"
                    title="The internal investigation id for your investigation"
                  />
                </div>
                <div class="form-group">
                  <label>Name</label>
                  <input
                    v-model="investigationName"
                    type="text"
                    maxlength="255"
                    class="form-control"
                    :disabled="loadingMessage"
                    required
                    @keyup.enter="submit"
                    data-bs-toggle="tooltip"
                    data-bs-delay="500"
                    title="Unique name for your investigation"
                  />
                  <div class="invalid-feedback">Please provide a name.</div>
                </div>
                <div class="form-group">
                  <label>Description</label>
                  <textarea
                    v-model="investigationDescription"
                    maxlength="3000"
                    rows="6"
                    class="form-control"
                    :disabled="loadingMessage"
                    required
                    @keyup.enter="submit"
                    placeholder="A short description for your investigation"
                    data-bs-toggle="tooltip"
                    data-bs-delay="500"
                    title="A short description for your investigation"
                  />
                  <small class="form-text text-muted"
                    >{{ formatter.number.default(investigationDescriptionLength) }} of {{ formatter.number.default(3000) }} characters</small
                  >
                  <div class="invalid-feedback">Please provide a description.</div>
                </div>
                <div class="form-group">
                  <label>Interview Template</label>
                  <select v-model="investigationInterviewId" class="form-control" :disabled="loadingMessage" required>
                    <option value selected hidden disabled>Select an Inteview Template</option>
                    <option v-for="f in organizationInterviews" v-bind:key="f.id" v-bind:value="f.id">
                      {{ f.displayName }}
                    </option>
                    <option value="custom">Custom Categories</option>
                  </select>
                  <div class="invalid-feedback">Please select an interview.</div>
                </div>
                <div class="form-group">
                  <InterviewCategories
                    ref="custom-categories"
                    v-if="isCustomizingInterviewCategories"
                    :organizationId="organizationId"
                    @test-categories-selected-changed="onCustomTestCategoriesChanged"
                  />
                </div>
                <div class="form-group">
                  <label>Case Type</label>
                  <select v-model="investigationCaseTypeCategoryCode" class="form-control" :disabled="loadingMessage || isCaseTypeCategoriesDisabled" required>
                    <option value selected hidden disabled>Select a Case Type</option>
                    <option :value="s.code" v-for="s in investigationCaseTypeCategories" :key="s.code">
                      {{ s.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a case type.</div>
                </div>
                <div class="form-group">
                  <label>Country</label>
                  <select v-model="investigationCountryCode" class="form-control" :disabled="loadingMessage" required>
                    <option value selected hidden disabled>Select a country</option>
                    <option :value="c.code" v-for="c in countries" :key="c.code">
                      {{ c.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a country.</div>
                </div>
                <div class="form-group" v-if="investigationCountryCode">
                  <label>State</label>
                  <select v-model="investigationStateCode" class="form-control" :disabled="loadingMessage" required>
                    <option value selected hidden disabled>Select a state</option>
                    <option :value="s.code" v-for="s in countryStates" :key="s.code">
                      {{ s.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a state.</div>
                </div>
                <div class="form-group">
                  <label>Function/District</label>
                  <select
                    v-model="investigationFunctionId"
                    class="form-control"
                    :disabled="loadingMessage || organizationFunctionsDisabled"
                    :required="!organizationFunctionsDisabled"
                  >
                    <option value selected hidden disabled>{{ organizationFunctionsDisabled ? "N/A" : "Select a function/district" }}</option>
                    <option v-for="f in organizationFunctions" v-bind:key="f.id" v-bind:value="f.id">
                      {{ f.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a function/district.</div>
                </div>
                <div class="form-group">
                  <label>Region</label>
                  <select
                    v-model="investigationRegionId"
                    class="form-control"
                    :disabled="loadingMessage || organizationRegionsDisabled"
                    :required="!organizationRegionsDisabled"
                  >
                    <option value selected hidden disabled>{{ organizationRegionsDisabled ? "N/A" : "Select a region" }}</option>
                    <option v-for="r in organizationRegions" v-bind:key="r.id" v-bind:value="r.id">
                      {{ r.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a region.</div>
                </div>
                <div class="form-group">
                  <label>Facility/Store</label>
                  <select
                    v-model="investigationFacilityId"
                    class="form-control"
                    :disabled="loadingMessage || organizationFacilitiesDisabled"
                    :required="!organizationFacilitiesDisabled"
                  >
                    <option value selected hidden disabled>{{ organizationFacilitiesDisabled ? "N/A" : "Select a facility/store" }}</option>
                    <option v-for="f in organizationFacilities" v-bind:key="f.id" v-bind:value="f.id">
                      {{ f.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a facility/store.</div>
                </div>
                <div class="form-group">
                  <label>Company/Division</label>
                  <select
                    v-model="investigationCompanyId"
                    class="form-control"
                    :disabled="loadingMessage || organizationCompaniesDisabled"
                    :required="!organizationCompaniesDisabled"
                  >
                    <option value selected hidden disabled>{{ organizationCompaniesDisabled ? "N/A" : "Select a company/division" }}</option>
                    <option v-for="f in organizationCompanies" v-bind:key="f.id" v-bind:value="f.id">
                      {{ f.displayName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">Please select a company/division.</div>
                </div>
                <div class="form-group">
                  <label>Collaborators</label>
                  <multiselect
                    v-model="investigationCollaborators"
                    label="fullName"
                    track-by="userId"
                    placeholder="Type to search"
                    open-direction="bottom"
                    :options="organizationUsers"
                    :multiple="true"
                    :searchable="true"
                    :loading="organizationUsersLoading"
                    :internal-search="false"
                    :clear-on-select="false"
                    :close-on-select="false"
                    :options-limit="300"
                    :limit="3"
                    :limit-text="investigationCollaboratorsLimitText"
                    :max-height="600"
                    :show-no-results="false"
                    :hide-selected="true"
                    @search-change="getOrganizationCollaborators"
                  >
                    <template slot="tag" slot-scope="{ option, remove }"
                      ><span class="badge badge-primary p-1 mr-1"
                        ><span>{{ option.fullName }}</span
                        ><fa icon="times" class="cursor-pointer ml-1" @click="remove(option)"/></span
                    ></template>
                  </multiselect>
                  <small class="form-text text-muted">Optional: You can add and remove collaborators later.</small>
                </div>
                <div
                  class="modal fade"
                  id="duplicateReferenceCodeModal"
                  data-backdrop="static"
                  tabindex="-1"
                  role="dialog"
                  aria-labelledby="duplicateReferenceCodeLabel"
                  aria-hidden="true"
                >
                  <div class="modal-dialog" role="document">
                    <div class="modal-content">
                      <div class="modal-header">
                        <h5 class="modal-title" id="duplicateReferenceCodeLabel">Warning: Duplicate Investigation ID Detected</h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </div>
                      <div class="modal-body">
                        <p>
                          Please note that the Investigation ID you have entered has previously been used by this organization on
                          {{ formatter.date.short(this.previousReferenceCodeDate) }}.
                        </p>
                        <p>
                          If you wish to proceed, please click Create below. Otherwise, please provide a different Investigation ID.
                        </p>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-primary" data-dismiss="modal">Close</button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div class="card-footer">
              <div class="btn-group d-flex justify-content-around" role="group">
                <button type="button" class="btn btn-primary w-50 mr-2" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
                  <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  {{ loadingMessage ? loadingMessage : "Create" }}
                </button>
                <button type="button" class="btn btn-primary w-50 ml-2" @click="cancel" :disabled="loadingMessage">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import { mapGetters } from "vuex";
import formatter from "@/utils/formatter";
import NavBar from "@/views/reports/NavBar.vue";
import ErrorSummary from "@/components/ErrorSummary.vue";
import InterviewCategories from "@/views/reports/investigations/create-investigation/InterviewCategories.vue";
import $ from "jquery";

export default {
  components: {
    Multiselect,
    NavBar,
    ErrorSummary,
    InterviewCategories,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      // validations
      previousReferenceCodeDate: null,

      // enums
      investigationCaseTypeCategories: [],

      // org enums
      organizationCompanies: [],
      organizationFacilities: [],
      organizationRegions: [],
      organizationFunctions: [],
      organizationInterviews: [],

      // users
      organizationUsers: [],
      organizationUsersLoading: false,

      // form fields
      investigationName: null,
      investigationDescription: null,
      investigationReferenceCode: null,
      investigationCaseTypeCategoryCode: null,
      investigationCountryCode: null,
      investigationStateCode: null,
      investigationCompanyId: null,
      investigationFacilityId: null,
      investigationRegionId: null,
      investigationFunctionId: null,
      investigationInterviewId: null,
      investigationCollaborators: [],
    };
  },
  watch: {
    error() {
      if (this.error) {
        window.scrollTo(0, 0);
      }
    },
    investigationInterviewId() {
      this.onInvestigationInterviewIdSelected();
    },
  },
  computed: {
    ...mapGetters("user", ["user"]),
    ...mapGetters("static", ["countries", "states"]),
    ...mapGetters("role", ["isVerensicsAdministrator", "isOrganizationAdministrator"]),
    formatter: () => formatter,
    countryStates() {
      return this.states.filter(s => s.countryCode === this.investigationCountryCode);
    },
    investigationDescriptionLength() {
      return this.investigationDescription ? this.investigationDescription.length : 0;
    },
    organizationCompaniesDisabled() {
      return this.organizationCompanies.length == 0;
    },
    organizationFacilitiesDisabled() {
      return this.organizationFacilities.length == 0;
    },
    organizationRegionsDisabled() {
      return this.organizationRegions.length == 0;
    },
    organizationFunctionsDisabled() {
      return this.organizationFunctions.length == 0;
    },
    isCustomizingInterviewCategories() {
      return this.investigationInterviewId == "custom";
    },
    isCaseTypeCategoriesDisabled() {
      return !this.investigationCaseTypeCategories || !this.investigationCaseTypeCategories.length;
    },
  },
  created() {
    this.loadingMessage = "Loading...";
    const promises = [
      this.getOrganizationFacilities(),
      this.getOrganizationRegions(),
      this.getOrganizationFunctions(),
      this.getOrganizationCompanies(),
      this.getOrganizationInterviews(),
      this.getOrganizationCollaborators(),
    ];
    Promise.all(promises).then(() => (this.loadingMessage = null));
  },
  methods: {
    cancel() {
      this.$router.push(`/organizations/${this.organizationId}/investigations`);
    },
    reset() {
      this.loadingMessage = null;
      this.formWasValidated = false;
      this.error = null;
    },
    submit() {
      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // collaborator user ids
      var collaboratorUserIds = this.investigationCollaborators.map(u => u.userId);

      // payload
      const payload = {
        referenceCode: this.investigationReferenceCode,
        name: this.investigationName,
        description: this.investigationDescription,
        caseTypeCategoryCode: this.investigationCaseTypeCategoryCode,
        countryCode: this.investigationCountryCode,
        stateCode: this.investigationStateCode,
        organizationFacilityId: this.investigationFacilityId,
        organizationCompanyId: this.investigationCompanyId,
        organizationRegionId: this.investigationRegionId,
        organizationFunctionId: this.investigationFunctionId,
        collaboratorUserIds: collaboratorUserIds,
      };

      // interview template / custom categories
      if (this.isCustomizingInterviewCategories) {
        payload.customInterviewTestId = this.$refs["custom-categories"].getSelectedTestId();
        payload.customInterviewCategories = this.$refs["custom-categories"].getSelectedCategories().map(c => ({
          id: c.id,
          isEmphasized: c.isEmphasized,
        }));
      } else {
        payload.organizationInterviewId = this.investigationInterviewId;
      }

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/investigations`, payload)
        .then(res => {
          var investigationId = res.data.investigationId;
          this.$router.push(`/organizations/${this.organizationId}/investigations/${investigationId}`);
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    getInvestigationReferenceCodeDuplicate() {
      var encodedValue = encodeURIComponent(this.investigationReferenceCode);
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/investigations/reference-codes/${encodedValue}/duplicate`)
        .then(res => {
          var data = res.data;
          if (data.count > 0 && data.previousDate) {
            this.previousReferenceCodeDate = data.previousDate;
            $("#duplicateReferenceCodeModal").modal("show");
          }
        })
        .catch(err => (this.error = err));
    },
    getOrganizationCompanies() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/companies`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.organizationCompanies = items))
        .catch(err => (this.error = err));
    },
    getOrganizationFacilities() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/facilities`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.organizationFacilities = items))
        .catch(err => (this.error = err));
    },
    getOrganizationRegions() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/regions`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.organizationRegions = items))
        .catch(err => (this.error = err));
    },
    getOrganizationFunctions() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/functions`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.organizationFunctions = items))
        .catch(err => (this.error = err));
    },
    getOrganizationInterviews() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/interviews`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.organizationInterviews = items))
        .catch(err => (this.error = err));
    },
    getOrganizationCollaborators(query) {
      var encodedQuery = query ? encodeURIComponent(query) : "";
      this.organizationUsersLoading = true;
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/investigations/collaborators?query=${encodedQuery}`)
        .then(res => (this.organizationUsers = res.data.items.filter(i => i.userId !== this.user.id)))
        .then(() => (this.organizationUsersLoading = false))
        .catch(err => (this.error = err));
    },
    investigationCollaboratorsLimitText(count) {
      return `and ${count} other collaborators`;
    },
    sortByDisplayName(items) {
      return items.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    },
    filterEnabled(items) {
      if (this.isVerensicsAdministrator || this.isOrganizationAdministrator) return items;
      return items.filter(item => item.status === "enabled");
    },
    onInvestigationInterviewIdSelected() {
      if (this.isCustomizingInterviewCategories) return;
      this.investigationCaseTypeCategoryCode = null;
      this.investigationCaseTypeCategories = [];
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/interviews/${this.investigationInterviewId}/attributes`)
        .then(res => (this.investigationCaseTypeCategories = res.data.categories))
        .catch(err => (this.error = err));
    },
    onCustomTestCategoriesChanged(testCategories) {
      this.investigationCaseTypeCategoryCode = null;
      this.investigationCaseTypeCategories = testCategories;
    },
  },
};
</script>
